import React from "react";
import { Link } from "react-router-dom";
import { Package, Leaf, Dumbbell, Coffee, Check, ArrowRight } from "lucide-react";
import { useSiteContent, getSection } from "../../context/SiteContentContext";

const ICON_BY_ID = {
    fruit: Leaf,
    detox: Leaf,
    protein: Dumbbell,
    tea: Coffee,
};

const STYLE_BY_ID = {
    fruit: { bg: "bg-brand-orange-soft", accent: "text-brand-orange" },
    detox: { bg: "bg-brand-green-soft", accent: "text-brand-green" },
    protein: { bg: "bg-brand-pink", accent: "text-[#CFA059]" },
    tea: { bg: "bg-brand-yellow", accent: "text-[#8691A0]" },
};

const FALLBACK_LINES = [
    {
        id: "fruit",
        name: "FRUIT",
        subtitle: "A linha base",
        description:
            "Polpa congelada das melhores frutas. Morango, Manga, Maracujá, Abacaxi, Frutos Vermelhos, Limonada e mais.",
        image: "/brand/pack-morango-real.png",
        mark: "/brand/logo-main.png",
        color: "#B95C50",
        features: [
            "Doses individuais ou bolsas a granel",
            "100% fruta, sem açúcar adicionado",
            "Servido frio, preparado em segundos",
        ],
    },
    {
        id: "detox",
        name: "DETOX",
        subtitle: "Vegetais + fruta verde",
        description:
            "Mistura de frutas e vegetais de folha verde. Leve, funcional e alinhada com clientes conscientes.",
        image: "/brand/line-detox.jpg",
        mark: "/brand/lines/detox.png",
        color: "#7A8B76",
        features: [
            "Base de maçã, pepino e espinafre",
            "Rico em clorofila e antioxidantes",
            "Ideal para ginásios e SPAs",
        ],
    },
    {
        id: "protein",
        name: "PROTEIN",
        subtitle: "Fruta + proteína",
        description:
            "Polpa de fruta enriquecida com proteína vegetal. Pronto a servir como recuperador pós-treino.",
        image:
            "https://images.unsplash.com/photo-1623428454614-abaf00244e52?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
        mark: "/brand/lines/protein.png",
        color: "#CFA059",
        features: [
            "25 g de proteína vegetal por dose",
            "Sem lactose, sem glúten",
            "Perfeito para ginásios e clubes",
        ],
    },
    {
        id: "tea",
        name: "TEA",
        subtitle: "Infusões frias",
        description:
            "Chás gelados com polpa de fruta. Uma alternativa natural e premium aos refrigerantes.",
        image:
            "https://images.unsplash.com/photo-1556679343-c7306c1976bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
        mark: "/brand/lines/tea.png",
        color: "#8691A0",
        features: [
            "Chá verde, preto, rooibos ou hibiscos",
            "Combinados com polpa de fruta",
            "Baixo em cafeína, zero artificial",
        ],
    },
];

export const ProductLines = () => {
    const { content } = useSiteContent();
    const cmsLines = getSection(content, "lines", {}).items;
    const lines =
        Array.isArray(cmsLines) && cmsLines.length > 0 ? cmsLines : FALLBACK_LINES;

    return (
        <section
            id="linhas"
            data-testid="product-lines"
            className="py-20 md:py-28 relative overflow-hidden bg-brand-cream"
        >
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.04] bg-hex-pattern pointer-events-none"
                style={{ backgroundImage: "url('/brand/pattern-3.png')" }}
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
                            <Package className="h-3.5 w-3.5" />
                            Linhas de produto
                        </span>
                        <h2 className="mt-3 font-display font-medium text-4xl md:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight">
                            Quatro linhas.{" "}
                            <span className="italic text-brand-orange">Uma filosofia.</span>
                        </h2>
                        <p className="mt-4 text-lg text-brand-ink-soft">
                            Polpa congelada em bolsas seladas de marca. Dose a dose ou a
                            granel, ao ritmo de cada operação.
                        </p>
                    </div>
                </div>

                <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
                    {lines.map((line) => {
                        const Icon = ICON_BY_ID[line.id] || Leaf;
                        const style = STYLE_BY_ID[line.id] || {
                            bg: "bg-brand-cream",
                            accent: "text-brand-ink",
                        };
                        return (
                            <article
                                key={line.id}
                                data-testid={`line-${line.id}`}
                                className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-pop transition-all duration-300 hover:-translate-y-1 flex flex-col md:flex-row"
                            >
                                <div
                                    className={`relative md:w-2/5 aspect-[4/3] md:aspect-auto overflow-hidden ${style.bg}`}
                                >
                                    {line.image && (
                                        <img
                                            src={line.image}
                                            alt={`Linha ${line.name}`}
                                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                    )}
                                    <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 shadow-soft">
                                        <Icon className={`h-3.5 w-3.5 ${style.accent}`} />
                                        <span className="text-[11px] font-bold uppercase tracking-widest text-brand-ink">
                                            Linha {line.name}
                                        </span>
                                    </div>
                                    {line.mark && (
                                        <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-2xl shadow-soft p-2 h-16 w-16 flex items-center justify-center">
                                            <img
                                                src={line.mark}
                                                alt={`Marca ${line.name}`}
                                                className="max-h-full max-w-full object-contain"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1 p-6 md:p-8 flex flex-col">
                                    <div>
                                        <h3 className="font-display font-medium text-3xl md:text-4xl text-brand-ink leading-tight">
                                            {line.name}
                                        </h3>
                                        <p
                                            className={`mt-1 text-sm font-semibold ${style.accent}`}
                                        >
                                            {line.subtitle}
                                        </p>
                                    </div>
                                    <p className="mt-4 text-brand-ink-soft leading-relaxed text-sm md:text-base">
                                        {line.description}
                                    </p>
                                    <ul className="mt-5 space-y-2.5 flex-1">
                                        {(line.features || []).map((f) => (
                                            <li key={f} className="flex items-start gap-2 text-sm">
                                                <span
                                                    className={`mt-0.5 shrink-0 h-4 w-4 rounded-full flex items-center justify-center ${style.bg}`}
                                                >
                                                    <Check
                                                        className={`h-2.5 w-2.5 ${style.accent}`}
                                                    />
                                                </span>
                                                <span className="text-brand-ink-soft">{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        href="#contacto"
                                        data-testid={`line-cta-${line.id}`}
                                        className="mt-6 self-start inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand-green transition-colors"
                                    >
                                        Pedir informação →
                                    </a>
                                    <Link
                                        to={`/linhas/${line.id}`}
                                        data-testid={`line-detail-link-${line.id}`}
                                        className={`mt-2 self-start inline-flex items-center gap-1.5 text-sm font-bold ${style.accent} hover:underline transition-colors`}
                                    >
                                        Ver ficha completa <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
